// request_scope_change (versionless; WF-024). Reporting that the work differs from the
// permit stops the work at once. It does not cancel the permit or sign for the PH: the
// PH cancels separately, and the changed work needs a new permit.
import { useId, useState } from 'react'
import { RuleRefs } from '@/components/evidence'
import { Banner, Button, Field, TextArea } from '@/components/ui'
import type { ActionPanelProps } from '@/features/contracts'
import { PtwError } from '@/lib/commands'
import { useCommand } from '@/lib/queries'
import { buildScopeChangeArgs, expectedVersionFor, roleLabel } from '../logic'
import { CommandFailure, PanelFooter, Unanswered } from '../parts'

export function ScopeChangePanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const cmd = useCommand('request_scope_change')
  const descriptionId = useId()
  const [description, setDescription] = useState('')
  const failed = cmd.error ? PtwError.from(cmd.error) : null
  const missing = description.trim() ? [] : ['Describe what is different from the permit']

  const submit = async () => {
    const args = buildScopeChangeArgs(bundle.permit.id, description, action.role)
    if (!args) return
    try {
      await cmd.run(args, expectedVersionFor('request_scope_change', bundle))
      onDone()
    } catch {
      /* shown below */
    }
  }

  return (
    <div className="space-y-4">
      <Banner tone="bad" title="Reporting a scope change stops the work">
        <p>
          Work on this permit stops as soon as the report is recorded. A permit cannot be stretched to cover different work: the
          Permit Holder cancels this permit and the changed work needs a new permit.
          <RuleRefs ruleIds={['CORE-027', 'CORE-061', 'CORE-070']} />
        </p>
      </Banner>

      {!action.enabled ? (
        <Banner tone="warn" title="The server noted a problem">
          <p>{action.reason ?? 'No reason was given.'}</p>
          <p className="mt-1">You can still send the report. If it cannot be recorded you will see why.</p>
        </Banner>
      ) : null}

      <Field
        label="What is different from the permit?"
        required
        htmlFor={descriptionId}
        hint="The changed work, location, equipment or hazards. Facts only."
      >
        <TextArea
          id={descriptionId}
          rows={4}
          value={description}
          onChange={(e) => {
            if (cmd.error) cmd.resetIntent()
            setDescription(e.target.value)
          }}
          placeholder="e.g. Pipe supports SYN-PS-104 to 106 also need cleaning; they are not in this permit"
        />
      </Field>

      <p className="text-sm text-ink-soft">
        Your name, your role ({roleLabel(action.role)}) and the server time are recorded with the report. It is a report, not a
        cancellation: it does not sign anything for the Permit Holder.
      </p>

      {failed ? (
        <div className="space-y-2">
          <Banner tone="bad" title={failed.code === 'NETWORK' ? 'Scope change not confirmed' : 'Scope change NOT recorded'}>
            If the work no longer matches the permit, stop it anyway and tell the Permit Holder and the Responsible Supervisor directly.
          </Banner>
          <CommandFailure error={cmd.error} />
        </div>
      ) : null}

      <Unanswered items={missing} />

      <PanelFooter>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="danger" onClick={() => void submit()} loading={cmd.isPending} disabled={missing.length > 0}>
          Report scope change and stop work
        </Button>
      </PanelFooter>
    </div>
  )
}
